import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { SETTING_API_01 } from "../../api/ApiModel.tsx";
import { externalgetApi, externalpostApi } from "../../services/externalApis.tsx";
import {
  CON_STATUS_ACTIVE,
  CON_VALUE_ZERO,
  CON_MAX_NAME_LENGTH,
  CON_MAX_DESCRIPTION_LENGTH,
} from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
// Import messages
import {
  BrandMessages,
  UITextMessages,
  getFieldMaxLengthMessage,
  MessageCategory,
} from "../../utils/sys/Messages.tsx";

// Define interfaces
interface Brand {
  brandcode: number;
  brandname: string;
  descrip: string;
  COMPCODE?: string;
  LOCCODE?: string;
  STS?: string;
  ENUSER?: string;
}

interface UserParams {
  STAFFMAPLOCODE: string;
  USERNAME: string;
  COMPCODE: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface LocationState {
  selectedItem?: Brand;
  actionText?: string;
}

interface ErrorState {
  brandname?: string;
  descrip?: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

function CreateBrands(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const { selectedItem } = locationState || {};
  const isEdit = locationState?.actionText === "Edit";

  const SYS_USER = useSelector((state: any) => state.user) || {};
  const { STAFFMAPLOCODE, USERNAME, COMPCODE } = (SYS_USER.USER_PARAM as UserParams) || {};

  const [data, setData] = useState<Brand[]>([]);
  const [error, setError] = useState<ErrorState>({});
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.INFO | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const [selectedItemDetails, setSelectedItemDetails] = useState<Brand>({
    brandname: "",
    descrip: "",
    brandcode: CON_VALUE_ZERO,
  });

  // Load edit data
  useEffect(() => {
    if (selectedItem) {
      setSelectedItemDetails({
        brandname: selectedItem.brandname || "",
        descrip: selectedItem.descrip || "",
        brandcode: selectedItem.brandcode,
      });
    }
  }, [selectedItem]);

  useEffect(() => {
    setPageData({ title: "Brand" });
  }, [setPageData]);

  // Fetch brands
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const result = await externalgetApi(SETTING_API_01, "get", {
          COMPCODE,
          LOCCODE: STAFFMAPLOCODE,
          STS: CON_STATUS_ACTIVE,
        });
        setData(result || []);
      } catch (err) {
        console.error(err);
        setAlertType(MessageCategory.ERROR);
        setAlertMessage(BrandMessages.error.unexpected);
        setAlertOpen(true);
      }
    };
    fetchData();
  }, [COMPCODE, STAFFMAPLOCODE, BrandMessages.error.unexpected]);

  // Brand name input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    const noLeadingSpaceValue = value.replace(/^\s+/, "");
    let errorMsg = "";

    if (noLeadingSpaceValue.length === CON_MAX_NAME_LENGTH) {
      errorMsg = getFieldMaxLengthMessage("Brand Name", CON_MAX_NAME_LENGTH);
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setSelectedItemDetails((prev) => ({
      ...prev,
      [name]: noLeadingSpaceValue,
    }));
  };

  // Description input change
  const handleInputChangedes = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;

    // Remove leading spaces completely
    const noLeadingSpaceValue = value.replace(/^\s+/, "");

    let errorMsg = "";
    if (noLeadingSpaceValue.length === CON_MAX_DESCRIPTION_LENGTH) {
      errorMsg = getFieldMaxLengthMessage("Description", CON_MAX_DESCRIPTION_LENGTH);
    }

    setError((prev) => ({ ...prev, [name]: errorMsg }));
    setSelectedItemDetails((prev) => ({
      ...prev,
      [name]: noLeadingSpaceValue,
    }));
  };

  const handleCancel = (): void => {
    // Clear any error messages
    setError({});
    
    // Navigate back to the previous page
    navigate("/vcreatebrands");
  };

  const goBackToView = (): void => {
    navigate("/vcreatebrands");
  };

  // Confirm click
  const handleConfirmClick = async (): Promise<void> => {
    setIsButtonDisabled(true);

    const { brandname, descrip } = selectedItemDetails;
    const trimmedBrand = (brandname || "").trim();
    const trimmedDesc = (descrip || "").trim();

    let validationErrors: ErrorState = {};
    let hasError = false;

    // Required field validation
    if (!trimmedBrand) {
      validationErrors.brandname = BrandMessages.warning.nameRequired;
      hasError = true;
    }

    setError(validationErrors);

    if (hasError) {
      setAlertType(MessageCategory.ERROR);
      setAlertMessage(BrandMessages.warning.requiredFields);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    // Duplicate check
    const isDuplicate = data?.some(
      (item: Brand) =>
        item.brandname.toLowerCase() === trimmedBrand.toLowerCase() &&
        item.brandcode !== selectedItemDetails.brandcode
    );

    if (isDuplicate) {
      setAlertType(MessageCategory.WARNING);
      setAlertMessage(BrandMessages.warning.alreadyExists);
      setAlertOpen(true);
      setIsButtonDisabled(false);
      return;
    }

    // Save
    const payload: Brand = {
      brandname: trimmedBrand,
      descrip: trimmedDesc,
      brandcode: selectedItemDetails.brandcode,
      COMPCODE,
      LOCCODE: STAFFMAPLOCODE,
      STS: CON_STATUS_ACTIVE,
      ENUSER: USERNAME,
    };

    try {
      await externalpostApi(SETTING_API_01, payload);
      
      setAlertType(MessageCategory.SUCCESS);
      setAlertMessage(isEdit ? BrandMessages.success.update : BrandMessages.success.create);
      setAlertOpen(true);
      setTimeout(() => navigate("/vcreatebrands"), 2000);
    } catch (err) {
      const error = err as ApiError;
      console.error("API Error:", error);
      setAlertType(MessageCategory.ERROR);
      setAlertMessage(error.response?.data?.message || BrandMessages.error.unexpected);
      setAlertOpen(true);
    } finally {
      setIsButtonDisabled(false);
    }
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb}>
        <Tooltip title="Back to All Items" arrow sx={styles.breadcrumb_tooltip}>
          <IconButton
            id="btn_backtoview"
            aria-label="back"
            sx={styles.btn_breadcrumb_back}
            onClick={goBackToView}
          >
            <ArrowBackIcon
              id="ico_backtoview"
              sx={styles.btn_breadcrumb_back_icon}
            />
          </IconButton>
        </Tooltip>
        <Typography sx={styles.breadcrumb_divider}>|</Typography>

        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link
            underline="hover"
            sx={styles.breadcrumb_link}
            component="button"
          >
            {UITextMessages?.breadcrumb?.home || "Home"}
          </Link>
          <Link
            sx={styles.breadcrumb_link}
            underline="hover"
            component="button"
          >
            {UITextMessages?.breadcrumb?.settings || "Settings"}
          </Link>
          <Link
            sx={styles.breadcrumb_link_page}
            underline="hover"
            component="button"
            aria-current="page"
          >
            {isEdit ? "Edit Brands" : "Create Brands"}
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_add_page_container}>
        <AppBar sx={styles.appbar}>
          <Typography sx={styles.appbar_title}>
            {isEdit ? "Edit Brands" : "Create Brands"}
          </Typography>
        </AppBar>

        <Box sx={styles.add_form_page_container}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="txt_brandname"
                label="Brand Name"
                variant="outlined"
                fullWidth
                size="small"
                required
                name="brandname"
                value={selectedItemDetails.brandname}
                onChange={handleInputChange}
                error={!!error.brandname}
                helperText={error.brandname}
                inputProps={{ maxLength: CON_MAX_NAME_LENGTH }}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} />

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="txt_descrip"
                label="Description"
                variant="outlined"
                fullWidth
                size="small"
                name="descrip"
                value={selectedItemDetails.descrip}
                onChange={handleInputChangedes}
                error={!!error.descrip}
                helperText={error.descrip}
                inputProps={{ maxLength: CON_MAX_DESCRIPTION_LENGTH }}
                FormHelperTextProps={{
                  sx: { fontSize: "12px", color: "red" },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={styles.add_form_page_button_box}>
          <Button
            id="btn_cancel"
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            onClick={handleCancel}
          >
            Cancel
          </Button>

          <Button
            id="btn_confirm/modify"
            variant="contained"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            disabled={isButtonDisabled}
            onClick={handleConfirmClick}
          >
            {isEdit ? "Modify" : "Confirm"}
          </Button>
        </Box>

        <SystemAlert
          open={alertOpen}
          type={alertType}
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </Box>
    </>
  );
}

export default CreateBrands;